import {
  mockHospitals, mockDoctors, medicalServices, mockBookings,
  travelServices, faqItems, cities, allSpecialties, bookingStatusConfig,
  type Hospital, type Doctor, type MedicalService, type Booking,
  type BookingStatus, type TravelService, type FAQItem,
} from "@/data/mockData";

const delay = (ms = 300) => new Promise((r) => setTimeout(r, ms));

/**
 * Demo data now lives on the server (.demo-data/store.json) so bookings and
 * admin status changes are shared across devices. localStorage is kept only as
 * an offline fallback for the static preview build.
 */
const STORAGE_KEY = "chinacare_bookings";

function getStoredBookings(): Booking[] {
  if (typeof window === "undefined") return mockBookings;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return mockBookings;
}

function saveBookings(bookings: Booking[]) {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(bookings));
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T | null> {
  if (typeof window === "undefined") return null;
  try {
    const res = await fetch(url, { cache: "no-store", ...init });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

// Hospitals
export async function getHospitals(filters?: { city?: string; specialty?: string; search?: string }): Promise<Hospital[]> {
  await delay();
  const remote = await fetchJson<{ hospitals: Hospital[] }>("/api/hospitals");
  let result = remote?.hospitals ?? [...mockHospitals];
  if (filters?.city && filters.city !== "All") result = result.filter((h) => h.city === filters.city);
  if (filters?.specialty && filters.specialty !== "All") result = result.filter((h) => h.specialties.includes(filters.specialty!));
  if (filters?.search) {
    const q = filters.search.toLowerCase();
    result = result.filter((h) => h.name.toLowerCase().includes(q) || h.specialties.some((s) => s.toLowerCase().includes(q)));
  }
  return result;
}

export async function getHospitalById(id: number): Promise<Hospital | undefined> {
  await delay();
  const remote = await fetchJson<{ hospitals: Hospital[] }>("/api/hospitals");
  const list = remote?.hospitals ?? mockHospitals;
  return list.find((h) => h.id === id);
}

// Doctors
export async function getDoctors(filters?: { hospitalId?: number; specialty?: string; search?: string }): Promise<Doctor[]> {
  await delay();
  let result = [...mockDoctors];
  if (filters?.hospitalId) result = result.filter((d) => d.hospitalId === filters.hospitalId);
  if (filters?.specialty) result = result.filter((d) => d.specialties.includes(filters.specialty!));
  if (filters?.search) {
    const q = filters.search.toLowerCase();
    result = result.filter((d) => d.name.toLowerCase().includes(q) || d.specialties.some((s) => s.toLowerCase().includes(q)));
  }
  return result;
}

// Services
export async function getServices(category?: string): Promise<MedicalService[]> {
  await delay();
  if (category && category !== "All") return medicalServices.filter((s) => s.category === category);
  return medicalServices;
}

export async function getServiceById(id: string): Promise<MedicalService | undefined> {
  await delay();
  return medicalServices.find((s) => s.id === id);
}

// Bookings
export async function getBookings(): Promise<Booking[]> {
  await delay(500);
  const remote = await fetchJson<{ bookings: Booking[] }>("/api/bookings");
  if (remote) return remote.bookings;
  return getStoredBookings();
}

export async function submitBooking(data: Record<string, string>): Promise<{ success: boolean; bookingId: string }> {
  await delay(800);
  const remote = await fetchJson<{ booking: Booking }>("/api/bookings", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (remote) {
    return { success: true, bookingId: remote.booking.id };
  }

  // Offline fallback for the standalone preview only.
  const id = `BK-2026-${String(Math.floor(Math.random() * 900) + 100)}`;
  const service = medicalServices.find((s) => s.id === data.service);
  const newBooking: Booking = {
    id,
    serviceTitle: service?.title ?? data.service ?? "Unknown Service",
    hospitalName: data.city ? `Demo partner hospital in ${data.city}` : "Hospital coordination pending",
    doctorName: "To be assigned",
    preferredDate: data.date ?? "TBD",
    submittedAt: new Date().toISOString().split("T")[0],
    status: "pending",
    statusNote: "DEMO request: submitted and awaiting demo review.",
  };
  const bookings = getStoredBookings();
  bookings.unshift(newBooking);
  saveBookings(bookings);
  return { success: true, bookingId: id };
}

// Travel
export async function getTravelServices(): Promise<TravelService[]> {
  await delay();
  return travelServices;
}

// FAQ
export async function getFAQs(category?: string): Promise<FAQItem[]> {
  await delay();
  if (category && category !== "All") return faqItems.filter((f) => f.category === category);
  return faqItems;
}

// Meta
export { cities, allSpecialties, bookingStatusConfig };
export type { BookingStatus };
